import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    ChangeDetectionStrategy
} from '@angular/core';
import { ProblemCaseService } from '../service/problem-case-service';
import {
    ProblemCaseCreationInfo,
    Recipient,
} from '../model/problem-case-creation-info.model';
import {
    HisProblemCaseFormComponent,
    ProblemCaseFormValues,
    SenderEmailOption,
} from './problem-case-form/problem-case-form.component';
import { ProblemCase } from '../model/problem-case.model';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisAuthService } from '../../auth/service/his-auth.service';
import { HisProblemCaseSharedModuleOptions } from '../his-problem-case.options';
import { SendResult } from '../model/problem-case-send-result.model';
import { finalize, map, Observable, tap } from 'rxjs';

export const DISPLAY_BEHAVIOUR = [
    'inline',
    'textOnly',
    'iconOnly',
    'iconAndText',
] as const;

export type DisplayBehaviour = (typeof DISPLAY_BEHAVIOUR)[number];

@Component({
    selector: 'his-send-problem-case',
    templateUrl: './send-problem-case.component.html',
    styleUrl: './send-problem-case.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class HisSendProblemCaseComponent implements OnInit {
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private hisAuthService = inject(HisAuthService);
    private problemCaseService = inject(ProblemCaseService);
    private notificationService = inject(HisNotificationService);

    @Input({ required: true }) template!: string;
    @Input() recipientIds: number[];
    @Input() searchEntityIds?: number[];
    @Input() inlineSuccessMessageKey =
        'cs.psv.problemcase.send.successfulInlineMessageKey';
    @Input() successMessageKey = 'cs.psv.problemcase.send.successful';
    @Input() partialSuccessWithoutDetailsMessageKey =
        'cs.psv.problemcase.send.partiallySuccessfulWithoutDetails';
    @Input() partialSuccessWithDetailsMessageKey =
        'cs.psv.problemcase.send.partiallySuccessfulWithDetails';
    @Input() newProblemCaseMessageKey =
        'cs.psv.problemcase.action.newProblemCase';

    @Input() displayBehaviour: DisplayBehaviour = 'iconAndText';
    @Input() renderAsButton = false;

    /**
     * Whether to display the cancel button; Only applicable if `asDialog` is `false` Default is `true`.
     */
    @Input() showCancelButton = true;

    @Output() sendProblemCase = new EventEmitter<boolean>();
    @Output() cancelProblemCase = new EventEmitter<void>();

    @ViewChildren('formComponent')
    private formComponents!: QueryList<HisProblemCaseFormComponent>;

    protected dialogVisible = false;
    protected creationInfo: ProblemCaseCreationInfo | undefined = undefined;
    protected senderEmails: SenderEmailOption[] = []; // TODO get from creationInfo
    protected recipientsVisible = false;
    protected recipientsEditable = false;
    protected showSuccess = false;
    protected accessGranted = false;
    protected buttonText = this.newProblemCaseMessageKey;
    protected buttonClass = 'transparent';
    protected buttonIcon = 'COMMENT';
    protected disableForm = false;

    ngOnInit(): void {
        this.accessGranted = this.hisAuthService.hasAll(
            ...HisProblemCaseSharedModuleOptions.RIGHT_CREATE_PROBLEM_CASE,
        );

        // don't try to initialize anything if the user has no access
        if (!this.accessGranted) {
            return;
        }

        // check if the template is available for the current user and continue initialization only if it is available
        this.problemCaseService.isTemplateAvailableForCurrentUser(this.template).subscribe((available) => {
            this.accessGranted = available;
            if ( !this.accessGranted ) {
                return;
            }
            
            switch (this.displayBehaviour) {
                case 'iconOnly': {
                    this.buttonClass = this.renderAsButton ? undefined : 'icon';
                    this.buttonText = undefined;
                    this.buttonIcon = 'COMMENT';
                    break;
                }
                case 'textOnly': {
                    this.buttonClass = this.renderAsButton
                        ? undefined
                        : 'transparent';
                    this.buttonText = this.newProblemCaseMessageKey;
                    this.buttonIcon = undefined;
                    break;
                }
                case 'iconAndText': {
                    this.buttonClass = this.renderAsButton
                        ? undefined
                        : 'transparent';
                    this.buttonText = this.newProblemCaseMessageKey;
                    this.buttonIcon = 'COMMENT';
                    break;
                }
            }

            if (this.displayBehaviour === 'inline') {
                this.updateCreationInfo().subscribe();
            }
        });

    }

    private updateCreationInfo(): Observable<void> {
        // reset old values
        this.creationInfo = undefined;
        this.senderEmails = [];

        // query new values
        return this.problemCaseService
            .newInstance(
                this.template,
                this.recipientIds ?? [],
                this.searchEntityIds,
            )
            .pipe(
                tap((e) => {
                    this.creationInfo = e;
                    this.senderEmails = this.asSenderEmails(
                        this.creationInfo.senderEMailAddresses,
                    );
                    this.recipientsVisible =
                        this.creationInfo.recipientVisibility !== 0;
                    this.recipientsEditable =
                        this.recipientsVisible &&
                        this.creationInfo.recipientVisibility === 2;

                    // This needs to delayed as otherwise the formComponents will not yet be visible
                    // and thus no single form will be resetted.
                    // ~Perhaps there is a better way to do this, but as of now this is the best solution I can think of.~
                    // Nope, there isn't. I tried a subscription based approach, but that caused an ExpressionChangedAfterItHasBeenCheckedError.
                    setTimeout(() => this.resetFormComponents());
                }),
                map((_e: any) => undefined as void),
            );
    }

    private asSenderEmails(senderEMailAddresses: {
        [key: number]: string;
    }): SenderEmailOption[] {
        return Object.keys(senderEMailAddresses).map((key) => ({
            emailId: parseInt(key, 10),
            label: senderEMailAddresses[+key],
        }));
    }

    private resetFormComponents() {
        this.formComponents.forEach((formComponent) => formComponent.reset());
    }

    protected onShowDialog() {
        this.updateCreationInfo().subscribe(() => {
            this.dialogVisible = true;
        });
    }

    protected onSubmitProblemCase(formValues: ProblemCaseFormValues) {
        this.disableForm = true;
        const problemCase = new ProblemCase(
            this.template,
            this.searchEntityIds,
            formValues.senderEmailId,
            formValues.recipientIds,
            formValues.subject,
            formValues.content,
        );

        this.problemCaseService
            .send(problemCase)
            .pipe(finalize(() => (this.disableForm = false)))
            .subscribe((sendResult) => this.onSendSuccess(sendResult));
    }

    private onSendSuccess(sendResult: SendResult) {
        if (this.displayBehaviour === 'inline') {
            this.showSuccess = true;
            this.showSuccessMessages(sendResult);
        } else {
            this.dialogVisible = false;
            this.showSuccessMessages(sendResult);
        }

        this.sendProblemCase.emit(true);
    }

    private showSuccessMessages(sendResult: SendResult) {
        if (sendResult.sendToAllRecipientsSuccessful) {
            this.notificationService.successI18n(this.successMessageKey);
            return;
        }

        if (!this.recipientsVisible) {
            this.notificationService.infoI18n(
                this.partialSuccessWithoutDetailsMessageKey,
            );
            return;
        }

        const unsentRecipientPersons = sendResult.unsentRecipientPersons
            .map((person) => person.name)
            .join(', ');
        this.notificationService.infoI18n(
            this.partialSuccessWithDetailsMessageKey,
            unsentRecipientPersons,
        );
    }

    protected onCancelProblemCase(): void {
        this.resetFormComponents();
        this.dialogVisible = false;
        this.cancelProblemCase.emit();
    }

    protected findAllRecipients(): (
        autocompleteQuery: string,
    ) => Observable<Recipient[]> {
        return (autocompleteQuery: string) =>
            this.problemCaseService.findAllRecipients(
                this.template,
                this.searchEntityIds,
                autocompleteQuery,
            );
    }
}
